import type { CodeableConcept, Extension, Patient } from '@medplum/fhirtypes';

import type { ConceptOption } from '../../components/inputs';

/**
 * US Core patient demographics: extension URLs, curated closed value sets
 * for the edit variant's selects, and pure extension write helpers.
 * Terminology-service-backed value sets come later; these lists cover the
 * alpha's single-select editing.
 */

export const US_CORE_RACE_URL =
  'http://hl7.org/fhir/us/core/StructureDefinition/us-core-race';
export const US_CORE_ETHNICITY_URL =
  'http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity';
export const US_CORE_GENDER_IDENTITY_URL =
  'http://hl7.org/fhir/us/core/StructureDefinition/us-core-genderIdentity';
export const INDIVIDUAL_PRONOUNS_URL =
  'http://hl7.org/fhir/StructureDefinition/individual-pronouns';

const CDC_RACE_ETHNICITY_SYSTEM = 'urn:oid:2.16.840.1.113883.6.238';
const NULL_FLAVOR_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/v3-NullFlavor';
const MARITAL_STATUS_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus';
const CONTACT_RELATIONSHIP_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/v2-0063';
const SNOMED_SYSTEM = 'http://snomed.info/sct';
const LOINC_SYSTEM = 'http://loinc.org';
const BCP_47_SYSTEM = 'urn:ietf:bcp:47';

export const RACE_OPTIONS: ReadonlyArray<ConceptOption> = [
  {
    code: '1002-5',
    system: CDC_RACE_ETHNICITY_SYSTEM,
    display: 'American Indian or Alaska Native',
  },
  { code: '2028-9', system: CDC_RACE_ETHNICITY_SYSTEM, display: 'Asian' },
  {
    code: '2054-5',
    system: CDC_RACE_ETHNICITY_SYSTEM,
    display: 'Black or African American',
  },
  {
    code: '2076-8',
    system: CDC_RACE_ETHNICITY_SYSTEM,
    display: 'Native Hawaiian or Other Pacific Islander',
  },
  { code: '2106-3', system: CDC_RACE_ETHNICITY_SYSTEM, display: 'White' },
  { code: 'ASKU', system: NULL_FLAVOR_SYSTEM, display: 'Declined to answer' },
];

export const ETHNICITY_OPTIONS: ReadonlyArray<ConceptOption> = [
  {
    code: '2135-2',
    system: CDC_RACE_ETHNICITY_SYSTEM,
    display: 'Hispanic or Latino',
  },
  {
    code: '2186-5',
    system: CDC_RACE_ETHNICITY_SYSTEM,
    display: 'Not Hispanic or Latino',
  },
  { code: 'ASKU', system: NULL_FLAVOR_SYSTEM, display: 'Declined to answer' },
];

export const GENDER_IDENTITY_OPTIONS: ReadonlyArray<ConceptOption> = [
  { code: '446151000124109', system: SNOMED_SYSTEM, display: 'Male' },
  { code: '446141000124107', system: SNOMED_SYSTEM, display: 'Female' },
  { code: '33791000087105', system: SNOMED_SYSTEM, display: 'Non-binary' },
  { code: 'ASKU', system: NULL_FLAVOR_SYSTEM, display: 'Declined to answer' },
];

export const PRONOUN_OPTIONS: ReadonlyArray<ConceptOption> = [
  { code: 'LA29518-0', system: LOINC_SYSTEM, display: 'He / him' },
  { code: 'LA29519-8', system: LOINC_SYSTEM, display: 'She / her' },
  { code: 'LA29520-6', system: LOINC_SYSTEM, display: 'They / them' },
];

export const LANGUAGE_OPTIONS: ReadonlyArray<ConceptOption> = [
  { code: 'en', system: BCP_47_SYSTEM, display: 'English' },
  { code: 'es', system: BCP_47_SYSTEM, display: 'Spanish' },
  { code: 'zh', system: BCP_47_SYSTEM, display: 'Chinese' },
  { code: 'fr', system: BCP_47_SYSTEM, display: 'French' },
  { code: 'tl', system: BCP_47_SYSTEM, display: 'Tagalog' },
  { code: 'vi', system: BCP_47_SYSTEM, display: 'Vietnamese' },
  { code: 'ar', system: BCP_47_SYSTEM, display: 'Arabic' },
  { code: 'ko', system: BCP_47_SYSTEM, display: 'Korean' },
  { code: 'ru', system: BCP_47_SYSTEM, display: 'Russian' },
  { code: 'pt', system: BCP_47_SYSTEM, display: 'Portuguese' },
  { code: 'hi', system: BCP_47_SYSTEM, display: 'Hindi' },
];

export const MARITAL_STATUS_OPTIONS: ReadonlyArray<ConceptOption> = [
  { code: 'S', system: MARITAL_STATUS_SYSTEM, display: 'Single' },
  { code: 'M', system: MARITAL_STATUS_SYSTEM, display: 'Married' },
  { code: 'T', system: MARITAL_STATUS_SYSTEM, display: 'Domestic partner' },
  { code: 'D', system: MARITAL_STATUS_SYSTEM, display: 'Divorced' },
  { code: 'L', system: MARITAL_STATUS_SYSTEM, display: 'Legally separated' },
  { code: 'W', system: MARITAL_STATUS_SYSTEM, display: 'Widowed' },
];

export const CONTACT_RELATIONSHIP_OPTIONS: ReadonlyArray<ConceptOption> = [
  { code: 'SPO', system: CONTACT_RELATIONSHIP_SYSTEM, display: 'Spouse' },
  {
    code: 'DOM',
    system: CONTACT_RELATIONSHIP_SYSTEM,
    display: 'Domestic partner',
  },
  { code: 'PAR', system: CONTACT_RELATIONSHIP_SYSTEM, display: 'Parent' },
  { code: 'SIB', system: CONTACT_RELATIONSHIP_SYSTEM, display: 'Sibling' },
  { code: 'CHD', system: CONTACT_RELATIONSHIP_SYSTEM, display: 'Child' },
  { code: 'FND', system: CONTACT_RELATIONSHIP_SYSTEM, display: 'Friend' },
  { code: 'OTH', system: CONTACT_RELATIONSHIP_SYSTEM, display: 'Other' },
];

/** Index of the preferred communication entry, else the first. */
export function preferredLanguageIndex(patient: Patient): number {
  const index = (patient.communication ?? []).findIndex(
    (communication) => communication.preferred,
  );
  return index >= 0 ? index : 0;
}

/**
 * Set/clear the patient's preferred language. Updates the preferred (or
 * first) communication entry in place; clearing removes that entry and
 * leaves any other declared languages untouched.
 */
export function withPreferredLanguage(
  patient: Patient,
  language: CodeableConcept | undefined,
): Patient {
  const communication = [...(patient.communication ?? [])];
  if (language === undefined) {
    if (communication.length > 0) {
      communication.splice(preferredLanguageIndex(patient), 1);
    }
  } else if (communication.length > 0) {
    const index = preferredLanguageIndex(patient);
    communication[index] = { ...communication[index], language };
  } else {
    communication.push({ language, preferred: true });
  }
  return {
    ...patient,
    communication: communication.length > 0 ? communication : undefined,
  };
}

export type PatientContactUpdate = Partial<{
  name: string | undefined;
  relationship: CodeableConcept | undefined;
  phone: string | undefined;
}>;

/**
 * Edit the first Patient.contact entry (the profile's emergency contact).
 * Name is free text (contact.name.text); additional contacts are untouched.
 * A contact left with no fields is removed.
 */
export function withContact(
  patient: Patient,
  update: PatientContactUpdate,
): Patient {
  const contact = { ...(patient.contact?.[0] ?? {}) };
  if ('name' in update) {
    contact.name = update.name ? { text: update.name } : undefined;
  }
  if ('relationship' in update) {
    contact.relationship = update.relationship
      ? [update.relationship]
      : undefined;
  }
  if ('phone' in update) {
    const telecom = (contact.telecom ?? []).filter(
      (point) => point.system !== 'phone',
    );
    if (update.phone) telecom.push({ system: 'phone', value: update.phone });
    contact.telecom = telecom.length > 0 ? telecom : undefined;
  }
  const isEmpty = Object.values(contact).every((value) => value === undefined);
  const rest = patient.contact?.slice(1) ?? [];
  const next = isEmpty ? rest : [contact, ...rest];
  return { ...patient, contact: next.length > 0 ? next : undefined };
}

function withPatientExtension(
  patient: Patient,
  url: string,
  next: Extension | undefined,
): Patient {
  const rest = (patient.extension ?? []).filter(
    (extension) => extension.url !== url,
  );
  const extension = next ? [...rest, next] : rest;
  return {
    ...patient,
    extension: extension.length > 0 ? extension : undefined,
  };
}

/** Upsert/remove a flat valueCodeableConcept extension (gender identity, pronouns). */
export function withConceptExtension(
  patient: Patient,
  url: string,
  concept: CodeableConcept | undefined,
): Patient {
  return withPatientExtension(
    patient,
    url,
    concept ? { url, valueCodeableConcept: concept } : undefined,
  );
}

/** Upsert/remove a US Core race/ethnicity extension (nested ombCategory + text). */
export function withOmbExtension(
  patient: Patient,
  url: string,
  option: ConceptOption | undefined,
): Patient {
  return withPatientExtension(
    patient,
    url,
    option
      ? {
          url,
          extension: [
            {
              url: 'ombCategory',
              valueCoding: {
                system: option.system,
                code: option.code,
                display: option.display,
              },
            },
            { url: 'text', valueString: option.display },
          ],
        }
      : undefined,
  );
}
